'use client'

import useResizeObserver from '@/hooks/useResizeObserver'
import { cva } from '@/styled-system/css'
import { Box, Flex, type HTMLStyledProps } from '@/styled-system/jsx'
import { memo, useRef, useState } from 'react'
import ChipStack from '../shared/chipStack'
import { POCKETS } from './constants'
import RouletteGamePocket from './pocket'

const styles = cva({
  base: {
    position: 'relative',
    display: 'grid',
    gap: '0.25em',
    w: 'full',
    transitionDuration: 'normal',
    transitionProperty: 'opacity',
    zIndex: 0,
  },
  variants: {
    orientation: {
      horizontal: {
        gridTemplateAreas: `
          'number0 number3 number6 number9 number12 number15 number18 number21 number24 number27 number30 number33 number36 row1'
          'number0 number2 number5 number8 number11 number14 number17 number20 number23 number26 number29 number32 number35 row2'
          'number0 number1 number4 number7 number10 number13 number16 number19 number22 number25 number28 number31 number34 row3'
          '. range0112 range0112 range0112 range0112 range1324 range1324 range1324 range1324 range2536 range2536 range2536 range2536 .'
          '. range0118 range0118 parityEven parityEven red red black black parityOdd parityOdd range1936 range1936 .'
        `,
        gridTemplateColumns: 'repeat(14, minmax(1fr, 3em))',
        gridTemplateRows: 'repeat(5, 3em)',
        w: 'full',
        maxW: 'full',
        justifySelf: 'center',
      },
      vertical: {
        gridTemplateColumns: 'repeat(5, 3em)',
        gridTemplateRows: 'repeat(14, minmax(1fr, 3em))',
        gridTemplateAreas: `
          '. . number0 number0 number0'
          'range0118 range0112 number1 number2 number3'
          'range0118 range0112 number4 number5 number6'
          'parityEven range0112 number7 number8 number9'
          'parityEven range0112 number10 number11 number12'
          'red range1324 number13 number14 number15'
          'red range1324 number16 number17 number18'
          'black range1324 number19 number20 number21'
          'black range1324 number22 number23 number24'
          'parityOdd range2536 number25 number26 number27'
          'parityOdd range2536 number28 number29 number30'
          'range1936 range2536 number31 number32 number33'
          'range1936 range2536 number34 number35 number36'
          '. . row3 row2 row1'
        `,
        w: 'max-content',
        h: '60dvh',
        alignSelf: 'flex-end',
        justifySelf: 'flex-end',
      },
    },
    disabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
      },
    },
  },
})

const topSplits = [
  { id: 'group01_02', area: 'number1', numbers: [1, 2] },
  { id: 'group02_03', area: 'number2', numbers: [2, 3] },
  { id: 'group01_02_03', area: 'number3', numbers: [1, 2, 3] },
  { id: 'group04_05', area: 'number4', numbers: [4, 5] },
  { id: 'group05_06', area: 'number5', numbers: [5, 6] },
  { id: 'group04_05_06', area: 'number6', numbers: [4, 5, 6] },
  { id: 'group07_08', area: 'number7', numbers: [7, 8] },
  { id: 'group08_09', area: 'number8', numbers: [8, 9] },
  { id: 'group07_08_09', area: 'number9', numbers: [7, 8, 9] },
  { id: 'group10_11', area: 'number10', numbers: [10, 11] },
  { id: 'group11_12', area: 'number11', numbers: [11, 12] },
  { id: 'group10_11_12', area: 'number12', numbers: [10, 11, 12] },
  { id: 'group13_14', area: 'number13', numbers: [13, 14] },
  { id: 'group14_15', area: 'number14', numbers: [14, 15] },
  { id: 'group13_14_15', area: 'number15', numbers: [13, 14, 15] },
  { id: 'group16_17', area: 'number16', numbers: [16, 17] },
  { id: 'group17_18', area: 'number17', numbers: [17, 18] },
  { id: 'group16_17_18', area: 'number18', numbers: [16, 17, 18] },
  { id: 'group19_20', area: 'number19', numbers: [19, 20] },
  { id: 'group20_21', area: 'number20', numbers: [20, 21] },
  { id: 'group19_20_21', area: 'number21', numbers: [19, 20, 21] },
  { id: 'group22_23', area: 'number22', numbers: [22, 23] },
  { id: 'group23_24', area: 'number23', numbers: [23, 24] },
  { id: 'group22_23_24', area: 'number24', numbers: [22, 23, 24] },
  { id: 'group25_26', area: 'number25', numbers: [25, 26] },
  { id: 'group26_27', area: 'number26', numbers: [26, 27] },
  { id: 'group25_26_27', area: 'number27', numbers: [25, 26, 27] },
  { id: 'group28_29', area: 'number28', numbers: [28, 29] },
  { id: 'group29_30', area: 'number29', numbers: [29, 30] },
  { id: 'group28_29_30', area: 'number30', numbers: [28, 29, 30] },
  { id: 'group31_32', area: 'number31', numbers: [31, 32] },
  { id: 'group32_33', area: 'number32', numbers: [32, 33] },
  { id: 'group31_32_33', area: 'number33', numbers: [31, 32, 33] },
  { id: 'group34_35', area: 'number34', numbers: [34, 35] },
  { id: 'group35_36', area: 'number35', numbers: [35, 36] },
  { id: 'group34_35_36', area: 'number36', numbers: [34, 35, 36] },
]

const leftSplits = [
  { id: 'group00_01', area: 'number1', numbers: [0, 1] },
  { id: 'group00_02', area: 'number2', numbers: [0, 2] },
  { id: 'group00_03', area: 'number3', numbers: [0, 3] },
  { id: 'group01_04', area: 'number4', numbers: [1, 4] },
  { id: 'group02_05', area: 'number5', numbers: [2, 5] },
  { id: 'group03_06', area: 'number6', numbers: [3, 6] },
  { id: 'group04_07', area: 'number7', numbers: [4, 7] },
  { id: 'group05_08', area: 'number8', numbers: [5, 8] },
  { id: 'group06_09', area: 'number9', numbers: [6, 9] },
  { id: 'group07_10', area: 'number10', numbers: [7, 10] },
  { id: 'group08_11', area: 'number11', numbers: [8, 11] },
  { id: 'group09_12', area: 'number12', numbers: [9, 12] },
  { id: 'group10_13', area: 'number13', numbers: [10, 13] },
  { id: 'group11_14', area: 'number14', numbers: [11, 14] },
  { id: 'group12_15', area: 'number15', numbers: [12, 15] },
  { id: 'group13_16', area: 'number16', numbers: [13, 16] },
  { id: 'group14_17', area: 'number17', numbers: [14, 17] },
  { id: 'group15_18', area: 'number18', numbers: [15, 18] },
  { id: 'group16_19', area: 'number19', numbers: [16, 19] },
  { id: 'group17_20', area: 'number20', numbers: [17, 20] },
  { id: 'group18_21', area: 'number21', numbers: [18, 21] },
  { id: 'group19_22', area: 'number22', numbers: [19, 22] },
  { id: 'group20_23', area: 'number23', numbers: [20, 23] },
  { id: 'group21_24', area: 'number24', numbers: [21, 24] },
  { id: 'group22_25', area: 'number25', numbers: [22, 25] },
  { id: 'group23_26', area: 'number26', numbers: [23, 26] },
  { id: 'group24_27', area: 'number27', numbers: [24, 27] },
  { id: 'group25_28', area: 'number28', numbers: [25, 28] },
  { id: 'group26_29', area: 'number29', numbers: [26, 29] },
  { id: 'group27_30', area: 'number30', numbers: [27, 30] },
  { id: 'group28_31', area: 'number31', numbers: [28, 31] },
  { id: 'group29_32', area: 'number32', numbers: [29, 32] },
  { id: 'group30_33', area: 'number33', numbers: [30, 33] },
  { id: 'group31_34', area: 'number34', numbers: [31, 34] },
  { id: 'group32_35', area: 'number35', numbers: [32, 35] },
  { id: 'group33_36', area: 'number36', numbers: [33, 36] },
]

const cornerSplits = [
  { id: 'group00_01_02', area: 'number1', numbers: [0, 1, 2] },
  { id: 'group00_02_03', area: 'number2', numbers: [0, 2, 3] },
  { id: 'group00_01_02_03', area: 'number3', numbers: [0, 1, 2, 3] },
  { id: 'group01_02_04_05', area: 'number4', numbers: [1, 2, 4, 5] },
  { id: 'group02_03_05_06', area: 'number5', numbers: [2, 3, 5, 6] },
  {
    id: 'group01_02_03_04_05_06',
    area: 'number6',
    numbers: [1, 2, 3, 4, 5, 6],
  },
  { id: 'group04_05_07_08', area: 'number7', numbers: [4, 5, 7, 8] },
  { id: 'group05_06_08_09', area: 'number8', numbers: [5, 6, 8, 9] },
  {
    id: 'group04_05_06_07_08_09',
    area: 'number9',
    numbers: [4, 5, 6, 7, 8, 9],
  },
  { id: 'group07_08_10_11', area: 'number10', numbers: [7, 8, 10, 11] },
  { id: 'group08_09_11_12', area: 'number11', numbers: [8, 9, 11, 12] },
  {
    id: 'group07_08_09_10_11_12',
    area: 'number12',
    numbers: [7, 8, 9, 10, 11, 12],
  },
  { id: 'group10_11_13_14', area: 'number13', numbers: [10, 11, 13, 14] },
  { id: 'group11_12_14_15', area: 'number14', numbers: [11, 12, 14, 15] },
  {
    id: 'group10_11_12_13_14_15',
    area: 'number15',
    numbers: [10, 11, 12, 13, 14, 15],
  },
  { id: 'group13_14_16_17', area: 'number16', numbers: [13, 14, 16, 17] },
  { id: 'group14_15_17_18', area: 'number17', numbers: [14, 15, 17, 18] },
  {
    id: 'group13_14_15_16_17_18',
    area: 'number18',
    numbers: [13, 14, 15, 16, 17, 18],
  },
  { id: 'group16_17_19_20', area: 'number19', numbers: [16, 17, 19, 20] },
  { id: 'group17_18_20_21', area: 'number20', numbers: [17, 18, 20, 21] },
  {
    id: 'group16_17_18_19_20_21',
    area: 'number21',
    numbers: [16, 17, 18, 19, 20, 21],
  },
  { id: 'group19_20_22_23', area: 'number22', numbers: [19, 20, 22, 23] },
  { id: 'group20_21_23_24', area: 'number23', numbers: [20, 21, 23, 24] },
  {
    id: 'group19_20_21_22_23_24',
    area: 'number24',
    numbers: [19, 20, 21, 22, 23, 24],
  },
  { id: 'group22_23_25_26', area: 'number25', numbers: [22, 23, 25, 26] },
  { id: 'group23_24_26_27', area: 'number26', numbers: [23, 24, 26, 27] },
  {
    id: 'group22_23_24_25_26_27',
    area: 'number27',
    numbers: [22, 23, 24, 25, 26, 27],
  },
  { id: 'group25_26_28_29', area: 'number28', numbers: [25, 26, 28, 29] },
  { id: 'group26_27_29_30', area: 'number29', numbers: [26, 27, 29, 30] },
  {
    id: 'group25_26_27_28_29_30',
    area: 'number30',
    numbers: [25, 26, 27, 28, 29, 30],
  },
  { id: 'group28_29_31_32', area: 'number31', numbers: [28, 29, 31, 32] },
  { id: 'group29_30_32_33', area: 'number32', numbers: [29, 30, 32, 33] },
  {
    id: 'group28_29_30_31_32_33',
    area: 'number33',
    numbers: [28, 29, 30, 31, 32, 33],
  },
  { id: 'group31_32_34_35', area: 'number34', numbers: [31, 32, 34, 35] },
  { id: 'group32_33_35_36', area: 'number35', numbers: [32, 33, 35, 36] },
  {
    id: 'group31_32_33_34_35_36',
    area: 'number36',
    numbers: [31, 32, 33, 34, 35, 36],
  },
]

export type RouletteGameBoardProps = HTMLStyledProps<'div'> & {
  chips?: Record<string, number>
  disabled?: boolean
  onPlaceChip?: (pocketId: string) => void
  result?: number
  orientation?: 'horizontal' | 'vertical'
}

const RouletteGameBoard = memo(function RouletteGameBoard({
  chips = {},
  disabled: disabled,
  onPlaceChip,
  result,
  orientation = 'horizontal',
  ...props
}: RouletteGameBoardProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [highlightedNumbers, setHighlightedNumbers] = useState<
    number[] | undefined
  >()

  useResizeObserver(ref, (entries) => {
    const { width, height } = entries[0].contentRect
    const target = entries[0].target as HTMLElement
    target.style.fontSize = `${Math.max(width, height) / 724}em`
  })

  const handleMouseLeave = () => {
    setHighlightedNumbers(undefined)
  }

  return (
    <Box ref={ref} className={styles({ orientation, disabled })} {...props}>
      {POCKETS.map((pocket) => {
        const isHighlighted =
          highlightedNumbers !== undefined && pocket.numberValue !== undefined
            ? highlightedNumbers.includes(pocket.numberValue)
            : false

        return (
          <RouletteGamePocket
            key={pocket.id}
            area={pocket.id}
            variant={pocket.color}
            orientation={orientation}
            totalChips={chips[pocket.id]}
            highlighted={isHighlighted}
            active={result !== undefined && result === pocket.numberValue}
            onMouseEnter={() => setHighlightedNumbers(pocket.numbers)}
            onMouseLeave={handleMouseLeave}
            onClick={() => onPlaceChip?.(pocket.id)}
          >
            {pocket.label}
          </RouletteGamePocket>
        )
      })}
      {topSplits.map((split) => (
        <Flex
          key={split.id}
          align='center'
          justify='center'
          position='absolute'
          {...(orientation === 'horizontal'
            ? {
                top: '-0.125em',
                left: '50%',
                w: '2em',
                h: '1.25em',
              }
            : {
                top: '50%',
                left: 'calc(100% + 0.125em)',
                w: '1.25em',
                h: '2em',
              })}
          transform='translate(-50%, -50%)'
          _hover={{
            transform: 'translate(-50%, -50%) scale(1.2)',
          }}
          cursor='pointer'
          style={{ gridArea: split.area }}
          onMouseEnter={() => setHighlightedNumbers(split.numbers)}
          onMouseLeave={handleMouseLeave}
          onClick={() => onPlaceChip?.(split.id)}
        >
          <ChipStack amount={chips[split.id]} />
        </Flex>
      ))}
      {leftSplits.map((split) => (
        <Flex
          key={split.id}
          align='center'
          justify='center'
          position='absolute'
          {...(orientation === 'horizontal'
            ? {
                top: '50%',
                left: '-0.125em',
                w: '1.25em',
                h: '2em',
              }
            : {
                top: '-0.125em',
                left: '50%',
                w: '2em',
                h: '1.25em',
              })}
          transform='translate(-50%, -50%)'
          _hover={{
            transform: 'translate(-50%, -50%) scale(1.2)',
          }}
          cursor='pointer'
          style={{ gridArea: split.area }}
          onMouseEnter={() => setHighlightedNumbers(split.numbers)}
          onMouseLeave={handleMouseLeave}
          onClick={() => onPlaceChip?.(split.id)}
        >
          <ChipStack amount={chips[split.id]} />
        </Flex>
      ))}
      {cornerSplits.map((split) => (
        <Flex
          key={split.id}
          align='center'
          justify='center'
          position='absolute'
          top='-0.125em'
          {...(orientation === 'horizontal'
            ? {
                left: '-0.125em',
              }
            : {
                left: 'calc(100% + 0.125em)',
              })}
          w='1.25em'
          h='1.25em'
          transform='translate(-50%, -50%)'
          _hover={{
            transform: 'translate(-50%, -50%) scale(1.2)',
          }}
          cursor='pointer'
          style={{ gridArea: split.area }}
          onMouseEnter={() => setHighlightedNumbers(split.numbers)}
          onMouseLeave={handleMouseLeave}
          onClick={() => onPlaceChip?.(split.id)}
        >
          <ChipStack amount={chips[split.id]} />
        </Flex>
      ))}
    </Box>
  )
})

export default RouletteGameBoard
